<div class="settings-container">
  <!-- Header -->
  <div class="header-section">
    <div class="header-content">
      <h1 class="header-title">
        <span class="settings-icon">⚙️</span>
        Admin Settings
      </h1>
      <p class="header-subtitle">Configure system settings and manage feed history</p>
    </div>
  </div>

  <!-- Main Content -->
  <div class="settings-content" *ngIf="authService.isAdmin(); else notAdmin">
    <!-- Quick Actions Section -->
    <div class="settings-card quick-actions-card">
      <div class="card-header">
        <h2 class="card-title">
          <span class="card-icon">⚡</span>
          Quick Actions
        </h2>
        <p class="card-description">
          Quick access to frequently used admin tools and features.
        </p>
      </div>
      <div class="card-body">
        <div class="quick-actions-grid">
          <a routerLink="/relationship-calculator" class="quick-action-item relationship-action">
            <div class="action-icon">🔗</div>
            <div class="action-content">
              <h3>Relationship Calculator</h3>
              <p>Calculate relationships between family members</p>
            </div>
            <div class="action-arrow">→</div>
          </a>
        </div>
      </div>
    </div>

    <!-- History Retention Settings -->
    <div class="settings-card">
      <div class="card-header">
        <h2 class="card-title">
          <span class="card-icon">📅</span>
          History Retention Period
        </h2>
        <p class="card-description">
          Set the number of days to keep feed history before archiving. Posts older than this period will be archived when you run the archive process.
        </p>
      </div>

      <div class="card-body" *ngIf="!isLoading; else loadingState">
        <form [formGroup]="settingsForm" (ngSubmit)="saveSettings()">
          <div class="form-group">
            <label for="retentionPeriodDays" class="form-label">
              Retention Period (Days)
            </label>
            <div class="input-wrapper">
              <input
                type="number"
                id="retentionPeriodDays"
                formControlName="retentionPeriodDays"
                class="form-input"
                [class.error]="settingsForm.get('retentionPeriodDays')?.invalid && settingsForm.get('retentionPeriodDays')?.touched"
                min="1"
                max="3650"
                placeholder="Enter number of days"
              />
              <span class="input-suffix">days</span>
            </div>
            <div class="form-hint">
              Minimum: 1 day | Maximum: 3650 days (10 years)
            </div>
            <div class="form-error" *ngIf="settingsForm.get('retentionPeriodDays')?.invalid && settingsForm.get('retentionPeriodDays')?.touched">
              <span *ngIf="settingsForm.get('retentionPeriodDays')?.errors?.['required']">Retention period is required</span>
              <span *ngIf="settingsForm.get('retentionPeriodDays')?.errors?.['min']">Minimum value is 1 day</span>
              <span *ngIf="settingsForm.get('retentionPeriodDays')?.errors?.['max']">Maximum value is 3650 days</span>
            </div>
          </div>

          <!-- Current Settings Info -->
          <div class="info-box" *ngIf="currentSettings">
            <div class="info-item">
              <span class="info-label">Current Setting:</span>
              <span class="info-value">{{ currentSettings.retentionPeriodDays }} days</span>
            </div>
            <div class="info-item" *ngIf="currentSettings.lastUpdated">
              <span class="info-label">Last Updated:</span>
              <span class="info-value">{{ formatDate(currentSettings.lastUpdated) }}</span>
            </div>
          </div>

          <!-- Messages -->
          <div class="alert alert-error" *ngIf="error">
            <span class="alert-icon">⚠️</span>
            <span>{{ error }}</span>
          </div>
          <div class="alert alert-success" *ngIf="success">
            <span class="alert-icon">✅</span>
            <span>{{ success }}</span>
          </div>

          <!-- Actions -->
          <div class="form-actions">
            <button
              type="submit"
              class="btn btn-primary"
              [disabled]="isSaving || settingsForm.invalid"
            >
              <span *ngIf="!isSaving">💾 Save Settings</span>
              <span *ngIf="isSaving">⏳ Saving...</span>
            </button>
          </div>
        </form>
      </div>

      <ng-template #loadingState>
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading settings...</p>
        </div>
      </ng-template>
    </div>

    <!-- Archive Posts -->
    <div class="settings-card">
      <div class="card-header">
        <h2 class="card-title">
          <span class="card-icon">📦</span>
          Archive Old Posts
        </h2>
        <p class="card-description">
          Manually archive all posts that are older than the configured retention period. Archived posts will no longer appear in the feed but will be preserved in the database.
        </p>
      </div>

      <div class="card-body">
        <!-- Archive Result -->
        <div class="info-box success-box" *ngIf="archiveResult">
          <div class="info-item">
            <span class="info-label">Archived Posts:</span>
            <span class="info-value highlight">{{ archiveResult.archivedCount }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Cutoff Date:</span>
            <span class="info-value">{{ formatDate(archiveResult.cutoffDate) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Archived At:</span>
            <span class="info-value">{{ formatDate(archiveResult.archivedAt) }}</span>
          </div>
        </div>

        <div class="form-actions">
          <button
            type="button"
            class="btn btn-warning"
            (click)="archivePosts()"
            [disabled]="isArchiving"
          >
            <span *ngIf="!isArchiving">📦 Archive Posts Now</span>
            <span *ngIf="isArchiving">⏳ Archiving...</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Not Admin Message -->
  <ng-template #notAdmin>
    <div class="not-admin-message">
      <div class="message-icon">🚫</div>
      <h2>Access Denied</h2>
      <p>You must be an administrator to access this page.</p>
    </div>
  </ng-template>
</div>

